import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import {
  paths,
  ROLE_RANK,
  ReferralSettingsSchema,
  DEFAULT_ASK_TEXT,
  DEFAULT_REFERRAL_BLURB,
  DEFAULT_REFERRAL_HEADLINE,
  DEFAULT_SHARE_TEXT,
  RELAY_TAGLINE,
  REFERRAL_STATUS_LABEL,
  audienceLabel,
  rewardLabel,
  type CampaignAudience,
  type Referral,
  type ReferralCampaign,
  type ReferralSettings,
  type ReferralStatus,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { textareaClass } from "../../components/JobEditCard";
import { useDepartments } from "../../hooks/useDepartments";

// Referrals (Clevis Relay — good work, passed along). The office's view:
// rewards waiting to be paid, every referral as it moves (referred → booked
// → job done → reward due → rewarded), and the owner's rules: what the
// reward is, when it's earned, who's eligible, the words, the standing ask,
// and the company's own Tremendous account for gift cards.

type Row = Referral & { id: string };
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: c % 100 ? 2 : 0 });
const day = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const STATUS_CLASS: Record<ReferralStatus, string> = {
  referred: "bg-steel/15 text-ink",
  booked: "bg-ink/10 text-ink",
  done: "bg-ink/10 text-ink",
  reward_due: "bg-brandorange/15 text-brandorange-dark",
  paid: "bg-success/15 text-success",
  declined: "bg-steel/10 text-steel",
};

function Dollars({ cents, onChange, className }: { cents: number; onChange: (c: number) => void; className?: string }) {
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <span className="text-sm text-steel">$</span>
      <Input type="number" min={0} step={1} className="w-24 py-1 text-sm" value={cents / 100} onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value || 0) * 100)))} />
    </div>
  );
}

export function ReferralsPage() {
  const { tenantId, role } = useAuth();
  const isAdmin = ROLE_RANK[role ?? "staff"] >= ROLE_RANK.admin;
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<"all" | ReferralStatus>("all");
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantReferralSettings(tenantId)), (snap) => setSettings(ReferralSettingsSchema.parse(snap.data() ?? {}))),
      onSnapshot(query(collection(db, paths.tenantReferrals(tenantId)), orderBy("createdAt", "desc"), limit(400)), (snap) =>
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Referral) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const due = rows.filter((r) => r.status === "reward_due");
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  if (!tenantId || !settings) return <p className="text-sm text-steel">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-ink">Referrals</h1>
          <p className="text-xs text-steel">
            Clevis Relay · {RELAY_TAGLINE}{" "}
            <span className={cn("ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", settings.enabled ? "bg-success/15 text-success" : "bg-steel/15 text-steel")}>{settings.enabled ? "On" : "Off"}</span>
          </p>
        </div>
        {isAdmin ? (
          <Button type="button" variant={settings.enabled ? "ghost" : "primary"} className="px-3 py-1.5 text-xs" onClick={() => setShowSettings((v) => !v)}>
            {showSettings ? "Hide settings" : settings.enabled ? "Settings" : "Set it up"}
          </Button>
        ) : null}
      </div>

      {!settings.enabled && !showSettings ? (
        <Card>
          <CardTitle>Every happy customer, handing you the next one</CardTitle>
          <p className="text-sm text-ink/80">
            Turn it on and every customer gets their own link. It shows on their portal page, rides along in the "How did we do?" text, and a while after a paid job Clevis asks them
            once if they know anyone who needs you. When a friend books through the link and their first job is paid, the reward goes out on its own: a gift card through your own
            Tremendous account, or credit or cash the office marks paid.
          </p>
          {!isAdmin ? <p className="mt-2 text-xs text-steel">An admin or the owner switches it on.</p> : null}
        </Card>
      ) : null}

      {isAdmin && showSettings ? <SettingsPanel tenantId={tenantId} settings={settings} /> : null}

      {settings.enabled || rows.length ? (
        <>
          {due.length ? (
            <Card>
              <CardTitle>Rewards to pay ({due.length})</CardTitle>
              <div className="divide-y divide-steel/10 text-sm">
                {due.map((r) => (
                  <DueRow key={r.id} tenantId={tenantId} row={r} settings={settings} />
                ))}
              </div>
            </Card>
          ) : null}

          {rows.length ? <RelayNumbers rows={rows} /> : null}

          <CampaignsCard tenantId={tenantId} settings={settings} rows={rows} isAdmin={isAdmin} onOpenSettings={() => setShowSettings(true)} />

          <Card>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="mb-0">Referrals</CardTitle>
              <Select className="w-auto py-1 text-xs" aria-label="Stage" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
                <option value="all">All stages ({rows.length})</option>
                {(Object.keys(REFERRAL_STATUS_LABEL) as ReferralStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {REFERRAL_STATUS_LABEL[s]} ({counts[s] ?? 0})
                  </option>
                ))}
              </Select>
            </div>
            {shown.length === 0 ? (
              <p className="text-xs text-steel">
                {rows.length ? "Nothing at this stage." : "No referrals yet. Each customer's link is on their page (Customers → open one → Referrals) and in the portal."}
              </p>
            ) : (
              <div className="divide-y divide-steel/10 text-sm">
                {shown.map((r) => (
                  <div key={r.id} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_6.5rem_5rem_4rem] items-center gap-2 py-1.5">
                    <span className="min-w-0 truncate text-ink">
                      {r.customerId ? (
                        <Link href={`/customers/${r.customerId}`} className="hover:underline">
                          {r.friendName || "A friend"}
                        </Link>
                      ) : (
                        r.friendName || "A friend"
                      )}
                      {r.friendPhone || r.friendEmail ? <span className="ml-1 text-xs text-steel">{r.friendPhone || r.friendEmail}</span> : null}
                    </span>
                    <span className="min-w-0 truncate text-xs text-steel">
                      sent by{" "}
                      <Link href={`/customers/${r.referrerCustomerId}`} className="text-ink hover:underline">
                        {r.referrerName || "a customer"}
                      </Link>
                    </span>
                    <span className={cn("justify-self-start rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_CLASS[r.status])} title={r.declineReason || r.lastError || ""}>
                      {REFERRAL_STATUS_LABEL[r.status]}
                    </span>
                    <span className="text-right text-xs text-ink">
                      {r.expenseId ? (
                        <Link href={`/expenses/${r.expenseId}`} className="hover:underline" title="The expense on the friend's job">
                          {money(r.rewardCents)}
                        </Link>
                      ) : r.rewardCents ? (
                        money(r.rewardCents)
                      ) : r.invoiceTotalCents ? (
                        money(r.invoiceTotalCents)
                      ) : (
                        ""
                      )}
                    </span>
                    <span className="text-right text-xs text-steel">{day(r.paidAt ?? r.dueAt ?? r.doneAt ?? r.bookedAt ?? r.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardTitle>Where customers find their link</CardTitle>
            <ul className="list-disc space-y-1 pl-5 text-xs text-ink/80">
              <li>On their customer portal page, with copy, text and email buttons.</li>
              <li>{settings.askAfterJob ? "In the \"How did we do?\" text after a job." : "Not in the after-job text (switched off in settings)."}</li>
              <li>
                {settings.ask.enabled
                  ? `A text ${settings.ask.daysAfterPaid} days after a paid job, at most every ${settings.ask.everyMonths} months${settings.ask.homeownersOnly ? ", homeowners only" : ""}.`
                  : "The standing ask is off."}
              </li>
              <li>On each customer's page (Customers → open one → Referrals), where the office can copy it or text it to them.</li>
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/** The whole program at a glance (the latest 400 referrals). Reports → Referrals has it by period. */
function RelayNumbers({ rows }: { rows: Row[] }) {
  const won = rows.filter((r) => r.status === "done" || r.status === "reward_due" || r.status === "paid");
  const booked = rows.filter((r) => r.status === "booked").length + won.length;
  const revenue = won.reduce((s, r) => s + r.invoiceTotalCents, 0);
  const paid = rows.filter((r) => r.status === "paid");
  const rewards = paid.reduce((s, r) => s + r.rewardCents, 0);
  const stat = (label: string, value: string, hint?: string) => (
    <div className="min-w-[6.5rem]" title={hint}>
      <div className="text-lg font-bold leading-tight text-ink">{value}</div>
      <div className="text-[11px] text-steel">{label}</div>
    </div>
  );
  return (
    <Card>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {stat("Friends sent", String(rows.length), "Friends who came through a customer's link")}
        {stat("Booked", String(booked), "Friends whose job got on the calendar")}
        {stat("Done & paid", String(won.length), "First jobs finished and paid")}
        {stat("Revenue from them", money(revenue), "Their first invoices")}
        {stat("Rewards paid", `${money(rewards)}${paid.length ? ` · ${paid.length}` : ""}`, "What went out, and how many")}
        {stat("Per $1 in rewards", rewards ? money(Math.round((revenue / rewards) * 100)) : "—", "Revenue from referred friends for every dollar of rewards")}
      </div>
      <p className="mt-1 text-[11px] text-steel">
        <Link href="/reports" className="hover:underline">
          Reports → Referrals
        </Link>{" "}
        has this by period, with link opens, the conversion and your top referrers.
      </p>
    </Card>
  );
}

function DueRow({ tenantId, row, settings }: { tenantId: string; row: Row; settings: ReferralSettings }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const giftCard = row.rewardKind === "gift_card";
  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr("");
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-ink">
          <Link href={`/customers/${row.referrerCustomerId}`} className="font-semibold hover:underline">
            {row.referrerName || "A customer"}
          </Link>{" "}
          <span className="text-steel">sent</span> {row.friendName || "a friend"} · {money(row.rewardCents)}{" "}
          <span className="text-xs text-steel">{row.rewardKind === "credit" ? "credit" : row.rewardKind === "cash" ? "cash or check" : settings.tremendous.productLabel || "gift card"}</span>
        </div>
        <div className="text-xs text-steel">
          Earned {day(row.dueAt)}
          {row.invoiceTotalCents ? ` on a ${money(row.invoiceTotalCents)} invoice` : ""}.{" "}
          {giftCard ? (settings.tremendous.connected ? (row.lastError ? <span className="text-danger">Tremendous: {row.lastError}</span> : "Going out through Tremendous within minutes.") : "Connect Tremendous in settings to send gift cards, or pay it another way.") : ""}
        </div>
        {err ? <div className="text-xs text-danger">{err}</div> : null}
      </div>
      {giftCard && settings.tremendous.connected ? (
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => act(() => call("retryReferralReward", { tenantId, referralId: row.id }))}>
          Send now
        </Button>
      ) : (
        <>
          <Input className="w-40 py-1 text-xs" placeholder="Note (check #, applied to…)" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button type="button" className="px-2 py-1 text-xs" busy={busy} onClick={() => act(() => call("markReferralPaid", { tenantId, referralId: row.id, note }))}>
            Mark paid
          </Button>
        </>
      )}
      <ConfirmButton variant="ghost" className="px-2 py-1 text-xs text-steel" confirm="No reward for this one?" confirmLabel="Decline it" onConfirm={() => act(() => call("declineReferral", { tenantId, referralId: row.id, reason: note }))}>
        Decline
      </ConfirmButton>
    </div>
  );
}

function SettingsPanel({ tenantId, settings }: { tenantId: string; settings: ReferralSettings }) {
  const [form, setForm] = useState<ReferralSettings>(settings);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setForm(settings);
  }, [settings, dirty]);
  const set = (patch: Partial<ReferralSettings>) => {
    setDirty(true);
    setForm((f) => ({ ...f, ...patch }));
  };
  const preview = rewardLabel(form);

  async function save() {
    setBusy(true);
    setNote("");
    try {
      const { tremendous: _t, updatedAt: _u, ...rest } = form;
      await call("setReferralSettings", { tenantId, settings: rest });
      setDirty(false);
      setNote("Saved.");
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Settings</CardTitle>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          <span className="text-ink">Referral rewards are on</span>
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>The referrer gets</Label>
          <Select value={form.reward.kind} onChange={(e) => set({ reward: { ...form.reward, kind: e.target.value as ReferralSettings["reward"]["kind"] } })}>
            <option value="gift_card">A gift card (through your Tremendous account)</option>
            <option value="credit">Credit on their next job</option>
            <option value="cash">Cash or a check from the office</option>
          </Select>
          <div className="flex flex-wrap items-center gap-2">
            <Select className="w-auto py-1 text-sm" value={form.reward.basis} onChange={(e) => set({ reward: { ...form.reward, basis: e.target.value as "fixed" | "percent" } })}>
              <option value="fixed">A fixed amount</option>
              <option value="percent">A percent of the friend's first invoice</option>
            </Select>
            {form.reward.basis === "fixed" ? (
              <Dollars cents={form.reward.amountCents} onChange={(c) => set({ reward: { ...form.reward, amountCents: c } })} />
            ) : (
              <>
                <Input type="number" min={0} max={100} step={0.5} className="w-20 py-1 text-sm" value={form.reward.percentBps / 100} onChange={(e) => set({ reward: { ...form.reward, percentBps: Math.round(Number(e.target.value || 0) * 100) } })} />
                <span className="text-sm text-steel">% up to</span>
                <Dollars cents={form.reward.capCents} onChange={(c) => set({ reward: { ...form.reward, capCents: c } })} />
              </>
            )}
          </div>
          <p className="text-xs text-steel">Reads as: "{preview}".</p>
        </div>

        <div className="space-y-2">
          <Label>The new customer gets</Label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={form.friend.enabled} onChange={(e) => set({ friend: { ...form.friend, enabled: e.target.checked } })} />
            <span className="text-ink">A discount on their first estimate</span>
          </label>
          {form.friend.enabled ? <Dollars cents={form.friend.amountCents} onChange={(c) => set({ friend: { ...form.friend, amountCents: c } })} /> : null}
          <p className="text-xs text-steel">Added as its own line when the office makes the estimate from their request.</p>
        </div>

        <div className="space-y-2">
          <Label>The reward is earned when</Label>
          <Select value={form.payWhen} onChange={(e) => set({ payWhen: e.target.value as ReferralSettings["payWhen"] })}>
            <option value="invoice_paid">The friend's first invoice is paid in full</option>
            <option value="job_done">The friend's job is marked done</option>
          </Select>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-steel">No reward on a first invoice under</span>
            <Dollars cents={form.minInvoiceCents} onChange={(c) => set({ minInvoiceCents: c })} />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Who gets a link and a reward</Label>
          <Select value={form.eligible} onChange={(e) => set({ eligible: e.target.value as ReferralSettings["eligible"] })}>
            <option value="homeowners">Homeowners only (customers without a company name)</option>
            <option value="everyone">Every customer, builders too</option>
          </Select>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={form.askAfterJob} onChange={(e) => set({ askAfterJob: e.target.checked })} />
            <span className="text-ink">Put their link in the "How did we do?" text after a job</span>
          </label>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Landing page headline</Label>
          <Input value={form.headline} maxLength={140} placeholder={DEFAULT_REFERRAL_HEADLINE} onChange={(e) => set({ headline: e.target.value })} />
        </div>
        <div>
          <Label>Share text (what they send a friend)</Label>
          <Input value={form.shareText} maxLength={400} placeholder={DEFAULT_SHARE_TEXT} onChange={(e) => set({ shareText: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label>Landing page words</Label>
          <textarea className={cn(textareaClass, "min-h-16")} value={form.blurb} maxLength={600} placeholder={DEFAULT_REFERRAL_BLURB} onChange={(e) => set({ blurb: e.target.value })} />
          <p className="mt-0.5 text-[11px] text-steel">Blanks: {"{company} {referrer} {reward} {friendOffer} {link}"}. Leave a field empty for the standard words.</p>
        </div>
      </div>

      <div className="rounded-lg border border-steel/15 p-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={form.ask.enabled} onChange={(e) => set({ ask: { ...form.ask, enabled: e.target.checked } })} />
          <span className="font-semibold text-ink">The standing ask: text customers a while after a paid job</span>
        </label>
        {form.ask.enabled ? (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm text-steel">
              <Input type="number" min={1} max={365} className="w-16 py-1 text-sm" value={form.ask.daysAfterPaid} onChange={(e) => set({ ask: { ...form.ask, daysAfterPaid: Math.max(1, Number(e.target.value || 1)) } })} />
              days after a paid job, at most every
              <Input type="number" min={1} max={36} className="w-16 py-1 text-sm" value={form.ask.everyMonths} onChange={(e) => set({ ask: { ...form.ask, everyMonths: Math.max(1, Number(e.target.value || 1)) } })} />
              months, to customers with a paid job in the last
              <Input type="number" min={1} max={60} className="w-16 py-1 text-sm" value={form.ask.paidWithinMonths} onChange={(e) => set({ ask: { ...form.ask, paidWithinMonths: Math.max(1, Number(e.target.value || 1)) } })} />
              months.
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={form.ask.homeownersOnly} onChange={(e) => set({ ask: { ...form.ask, homeownersOnly: e.target.checked } })} />
              <span className="text-ink">Homeowners only (never builders)</span>
            </label>
            <Input value={form.ask.text} maxLength={400} placeholder={DEFAULT_ASK_TEXT} onChange={(e) => set({ ask: { ...form.ask, text: e.target.value } })} />
            <p className="text-[11px] text-steel">Goes out in your texting window, honors STOP, and "Reply STOP to opt out" is added.</p>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" busy={busy} disabled={!dirty} onClick={() => void save()}>
          Save
        </Button>
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>

      <TremendousPanel tenantId={tenantId} settings={settings} />
    </Card>
  );
}

function TremendousPanel({ tenantId, settings }: { tenantId: string; settings: ReferralSettings }) {
  const t = settings.tremendous;
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [products, setProducts] = useState<Array<{ id: string; name: string }> | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setNote("");
    try {
      await fn();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const load = () =>
    act(async () => {
      const r = await call<{ products: Array<{ id: string; name: string }>; balanceCents: number }>("listTremendousProducts", { tenantId }, { mutation: false });
      setProducts(r.products);
      setBalance(r.balanceCents);
    });

  return (
    <div className="space-y-2 rounded-lg border border-steel/15 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">Gift cards: your Tremendous account</span>
        {t.connected ? (
          <span className="text-xs text-success">
            Connected {t.summary}
            {t.sandbox ? " · sandbox (test cards, no money)" : ""}
          </span>
        ) : null}
      </div>
      {!t.connected ? (
        <>
          <p className="text-xs text-steel">
            Tremendous sends the gift cards from your own balance; Clevis never holds the money. Sign up at tremendous.com, fund the balance, then Team settings → Developers → add an
            API key and paste it here. A key that starts with TEST_ is their sandbox.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="password" className="w-72 py-1 text-sm" placeholder="Tremendous API key" value={key} autoComplete="off" onChange={(e) => setKey(e.target.value)} />
            <Button
              type="button"
              className="px-3 py-1 text-xs"
              busy={busy}
              disabled={key.trim().length < 10}
              onClick={() =>
                act(async () => {
                  const r = await call<{ balanceCents: number; products: Array<{ id: string; name: string }> }>("connectTremendous", { tenantId, apiKey: key.trim() });
                  setKey("");
                  setProducts(r.products);
                  setBalance(r.balanceCents);
                })
              }
            >
              Connect
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-steel">Card:</span>
            {products ? (
              <Select
                className="w-auto py-1 text-sm"
                value={t.productIds[0] ?? ""}
                onChange={(e) => {
                  const p = products.find((x) => x.id === e.target.value);
                  void act(() => call("setReferralSettings", { tenantId, settings: {}, giftCard: { productIds: p ? [p.id] : [], productLabel: p ? `${p.name} gift card` : "gift card" } }));
                }}
              >
                <option value="">Let them choose</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            ) : (
              <span className="text-ink">{t.productLabel || "their choice"}</span>
            )}
            <Button type="button" variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => void load()}>
              {products ? "Refresh" : "Change card / check balance"}
            </Button>
            {balance !== null ? <span className="text-xs text-steel">Balance {money(balance)}</span> : null}
          </div>
          <ConfirmButton variant="ghost" className="px-2 py-1 text-xs text-steel" confirm="Disconnect Tremendous? Gift cards stop going out until it's connected again." confirmLabel="Disconnect" onConfirm={() => act(() => call("disconnectTremendous", { tenantId }))}>
            Disconnect
          </ConfirmButton>
        </div>
      )}
      {note ? <p className="text-xs text-danger">{note}</p> : null}
    </div>
  );
}

// ---------- Campaigns: "ask for referrals" sends with their own audience ----------

type CampaignRow = ReferralCampaign & { id: string };
const when = (ms: number | null) => (ms ? new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const toLocalMs = (date: string, time: string): number | null => {
  if (!date) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = (time || "09:00").split(":").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, hh ?? 9, mm ?? 0).getTime();
};
const pad2 = (n: number) => String(n).padStart(2, "0");
const fromMs = (ms: number | null): { date: string; time: string } => {
  if (!ms) return { date: "", time: "09:00" };
  const d = new Date(ms);
  return { date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}` };
};

const EMPTY_CAMPAIGN = {
  name: "",
  channel: "text" as "text" | "email",
  text: "",
  subject: "",
  audience: { paidWithinMonths: 12, departmentIds: [] as string[], homeownersOnly: true, notAskedWithinDays: 90 },
  schedule: { kind: "once" as "once" | "repeat", at: null as number | null, everyMonths: 6 },
};
type CampaignDraft = typeof EMPTY_CAMPAIGN;

function CampaignsCard({ tenantId, settings, rows, isAdmin, onOpenSettings }: { tenantId: string; settings: ReferralSettings; rows: Row[]; isAdmin: boolean; onOpenSettings: () => void }) {
  const dept = useDepartments();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [editing, setEditing] = useState<{ id: string | null; draft: CampaignDraft } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  useEffect(
    () =>
      onSnapshot(query(collection(db, paths.tenantReferralCampaigns(tenantId)), orderBy("createdAt", "desc"), limit(100)), (snap) =>
        setCampaigns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ReferralCampaign) }))),
      ),
    [tenantId],
  );
  const deptNames = (ids: string[]) => ids.map((id) => dept.departments.find((d) => d.id === id)?.name ?? "?");
  const results = (id: string) => {
    const mine = rows.filter((r) => r.campaignId === id);
    return { sent: mine.length, booked: mine.filter((r) => r.status !== "referred" && r.status !== "declined").length, rewarded: mine.filter((r) => r.status === "paid").length };
  };
  async function act(id: string, fn: () => Promise<unknown>) {
    setBusyId(id);
    setNote("");
    try {
      await fn();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }
  const scheduleText = (c: CampaignRow) => {
    if (c.status === "draft") return "Draft";
    if (c.status === "paused") return "Paused";
    if (c.status === "done") return `Sent ${when(c.lastRunAt)}`;
    return c.schedule.kind === "repeat" ? `Every ${c.schedule.everyMonths} months · next ${when(c.nextRunAt)}` : `Runs ${when(c.nextRunAt)}`;
  };

  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">Campaigns</CardTitle>
        {isAdmin && !editing ? (
          <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setEditing({ id: null, draft: { ...EMPTY_CAMPAIGN, audience: { ...EMPTY_CAMPAIGN.audience }, schedule: { ...EMPTY_CAMPAIGN.schedule } } })}>
            New campaign
          </Button>
        ) : null}
      </div>
      <p className="mb-2 text-xs text-steel">Asks for referrals, each with its own audience and words. The standing ask is the default; campaigns are for a push.</p>
      <div className="divide-y divide-steel/10 text-sm">
        <div className="flex flex-wrap items-center gap-2 py-1.5">
          <span className="min-w-0 flex-1">
            <span className="font-semibold text-ink">Standing ask</span>{" "}
            <span className="text-xs text-steel">
              {settings.ask.enabled
                ? `${settings.ask.daysAfterPaid} days after a paid job, at most every ${settings.ask.everyMonths} months${settings.ask.homeownersOnly ? ", homeowners only" : ""}`
                : "off"}
            </span>
          </span>
          {isAdmin ? (
            <button type="button" className="text-xs text-steel hover:text-ink" onClick={onOpenSettings}>
              Edit in settings
            </button>
          ) : null}
        </div>
        {campaigns.map((c) => {
          const r = results(c.id);
          return (
            <div key={c.id} className="flex flex-wrap items-center gap-2 py-1.5">
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-ink">{c.name}</span>{" "}
                <span className="rounded-full bg-steel/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-ink">{c.channel}</span>{" "}
                <span className="text-xs text-steel">{audienceLabel(c.audience, deptNames(c.audience.departmentIds))}</span>
                <div className="text-xs text-steel">
                  {scheduleText(c)}
                  {c.totalQueued ? ` · asked ${c.totalQueued}` : ""}
                  {r.sent ? ` · ${r.sent} friend${r.sent === 1 ? "" : "s"} sent, ${r.booked} booked, ${r.rewarded} rewarded` : ""}
                </div>
              </span>
              {isAdmin ? (
                <span className="flex items-center gap-1">
                  <ConfirmButton variant="ghost" className="px-2 py-1 text-xs" confirm="Ask everyone in this audience now?" confirmLabel="Send now" busy={busyId === c.id} onConfirm={() => act(c.id, () => call("sendReferralCampaignNow", { tenantId, campaignId: c.id }))}>
                    Send now
                  </ConfirmButton>
                  {c.status === "scheduled" || c.status === "paused" ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      busy={busyId === c.id}
                      onClick={() =>
                        act(c.id, () =>
                          call("saveReferralCampaign", { tenantId, campaignId: c.id, campaign: { name: c.name }, status: c.status === "paused" ? "scheduled" : "paused" }),
                        )
                      }
                    >
                      {c.status === "paused" ? "Resume" : "Pause"}
                    </Button>
                  ) : null}
                  <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing({ id: c.id, draft: { name: c.name, channel: c.channel, text: c.text, subject: c.subject, audience: { ...c.audience }, schedule: { ...c.schedule } } })}>
                    Edit
                  </Button>
                  <ConfirmButton variant="ghost" className="px-2 py-1 text-xs text-steel" confirm="Delete this campaign?" confirmLabel="Delete" busy={busyId === c.id} onConfirm={() => act(c.id, () => call("deleteReferralCampaign", { tenantId, campaignId: c.id }))}>
                    Delete
                  </ConfirmButton>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {note ? <p className="mt-1 text-xs text-danger">{note}</p> : null}
      {editing ? (
        <CampaignEditor
          tenantId={tenantId}
          id={editing.id}
          draft={editing.draft}
          departments={dept.departments}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Card>
  );
}

function CampaignEditor({ tenantId, id, draft, departments, onClose }: { tenantId: string; id: string | null; draft: CampaignDraft; departments: Array<{ id: string; name: string }>; onClose: () => void }) {
  const [form, setForm] = useState<CampaignDraft>(draft);
  const [whenKind, setWhenKind] = useState<"now" | "date" | "repeat">(draft.schedule.kind === "repeat" ? "repeat" : draft.schedule.at ? "date" : "now");
  const [at, setAt] = useState(fromMs(draft.schedule.at));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string>("");
  const [err, setErr] = useState("");
  const set = (patch: Partial<CampaignDraft>) => setForm((f) => ({ ...f, ...patch }));
  const setA = (patch: Partial<CampaignAudience>) => setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));

  function payload() {
    const schedule = whenKind === "repeat" ? { kind: "repeat" as const, at: toLocalMs(at.date, at.time), everyMonths: form.schedule.everyMonths } : { kind: "once" as const, at: whenKind === "date" ? toLocalMs(at.date, at.time) : null, everyMonths: form.schedule.everyMonths };
    return { name: form.name.trim(), channel: form.channel, text: form.text, subject: form.subject, audience: form.audience, schedule };
  }
  async function save(status: "draft" | "scheduled") {
    if (!form.name.trim()) return setErr("Give it a name.");
    if (whenKind === "date" && !at.date) return setErr("Pick the date.");
    setBusy(true);
    setErr("");
    try {
      await call("saveReferralCampaign", { tenantId, campaignId: id, campaign: payload(), status });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function doPreview() {
    setBusy(true);
    setErr("");
    try {
      const r = await call<{ count: number; sample: string[]; off: boolean }>("previewReferralCampaign", { tenantId, audience: form.audience, channel: form.channel }, { mutation: false });
      setPreview(r.off ? "The referral program is off." : r.count === 0 ? "Nobody matches right now." : `Would reach ${r.count} customer${r.count === 1 ? "" : "s"}: ${r.sample.join(", ")}${r.count > r.sample.length ? "…" : ""}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-steel/15 p-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
        <div>
          <Label>Name</Label>
          <Input value={form.name} maxLength={80} placeholder="Spring push — fireplace customers" onChange={(e) => set({ name: e.target.value })} />
        </div>
        <div>
          <Label>Send by</Label>
          <Select value={form.channel} onChange={(e) => set({ channel: e.target.value as "text" | "email" })}>
            <option value="text">Text</option>
            <option value="email">Email</option>
          </Select>
        </div>
      </div>
      {form.channel === "email" ? (
        <div>
          <Label>Subject</Label>
          <Input value={form.subject} maxLength={120} placeholder="A note from your garage door team" onChange={(e) => set({ subject: e.target.value })} />
        </div>
      ) : null}
      <div>
        <Label>Message</Label>
        <textarea className={cn(textareaClass, "min-h-20")} value={form.text} maxLength={600} placeholder={DEFAULT_ASK_TEXT} onChange={(e) => set({ text: e.target.value })} />
        <p className="mt-0.5 text-[11px] text-steel">Blanks: {"{firstName} {company} {reward} {link}"}. Leave it empty for the standard ask. Texts get "Reply STOP to opt out" added.</p>
      </div>

      <div className="space-y-2 rounded-lg bg-cream/60 p-2">
        <Label className="mb-0">Who</Label>
        <div className="flex flex-wrap items-center gap-2 text-sm text-steel">
          <label className="flex cursor-pointer items-center gap-2 text-ink">
            <input type="checkbox" checked={form.audience.homeownersOnly} onChange={(e) => setA({ homeownersOnly: e.target.checked })} /> Homeowners only
          </label>
          <span>· with a paid job in the last</span>
          <Input type="number" min={0} max={60} className="w-16 py-1 text-sm" value={form.audience.paidWithinMonths} onChange={(e) => setA({ paidWithinMonths: Math.max(0, Number(e.target.value || 0)) })} />
          <span>months (0 = every customer) · not asked in the last</span>
          <Input type="number" min={0} max={365} className="w-16 py-1 text-sm" value={form.audience.notAskedWithinDays} onChange={(e) => setA({ notAskedWithinDays: Math.max(0, Number(e.target.value || 0)) })} />
          <span>days</span>
        </div>
        {departments.length && form.audience.paidWithinMonths > 0 ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-xs text-steel">Departments (any = leave all unticked):</span>
            {departments.map((d) => (
              <label key={d.id} className="flex cursor-pointer items-center gap-1.5 text-ink">
                <input
                  type="checkbox"
                  checked={form.audience.departmentIds.includes(d.id)}
                  onChange={(e) => setA({ departmentIds: e.target.checked ? [...form.audience.departmentIds, d.id] : form.audience.departmentIds.filter((x) => x !== d.id) })}
                />
                {d.name}
              </label>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => void doPreview()}>
            Who would get it?
          </Button>
          {preview ? <span className="text-xs text-ink">{preview}</span> : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-steel">
        <Label className="mb-0">When</Label>
        <Select className="w-auto py-1 text-sm" value={whenKind} onChange={(e) => setWhenKind(e.target.value as typeof whenKind)}>
          <option value="now">As soon as I schedule it</option>
          <option value="date">On a date</option>
          <option value="repeat">Repeat</option>
        </Select>
        {whenKind !== "now" ? (
          <>
            <Input type="date" className="w-40" value={at.date} onChange={(e) => setAt((a) => ({ ...a, date: e.target.value }))} />
            <Input type="time" className="w-32" value={at.time} onChange={(e) => setAt((a) => ({ ...a, time: e.target.value }))} />
          </>
        ) : null}
        {whenKind === "repeat" ? (
          <>
            <span>then every</span>
            <Input type="number" min={1} max={36} className="w-16 py-1 text-sm" value={form.schedule.everyMonths} onChange={(e) => set({ schedule: { ...form.schedule, everyMonths: Math.max(1, Number(e.target.value || 1)) } })} />
            <span>months</span>
          </>
        ) : null}
      </div>

      {err ? <p className="text-xs text-danger">{err}</p> : null}
      <div className="flex items-center gap-2">
        <Button type="button" busy={busy} onClick={() => void save("scheduled")}>
          {whenKind === "now" ? "Schedule (goes out now)" : "Schedule"}
        </Button>
        <Button type="button" variant="ghost" busy={busy} onClick={() => void save("draft")}>
          Save as draft
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
